import axios from 'axios';
import { useLocationStore } from 'gpcore-apikit/store/pinia/location';
import { type Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { onUnmounted, ref } from 'vue';
import useDatacenterSorting from './useDatacenterSorting';

export default () => {
  const isLoading = ref(false);
  const error = ref<Error | null>(null);
  // we have to keep an extra ref of the datacenters **and** not accessing from the store
  const datacenters = ref<Array<Datacenter>>([]);
  const locationStore = useLocationStore();
  const { addNotification } = useNotifications();

  const latencyEndpoints = ref<Array<string>>([]);
  const latestLatency = ref<Record<string, number>>({});
  const latencyInterval = ref<ReturnType<typeof setTimeout>>();
  const { sortDatacenters, sortedDatacenters } = useDatacenterSorting(datacenters, latestLatency);
  const updateLatencies = () => {
    return latencyEndpoints.value.map((endpoint: string) => {
      return axios
        .get(endpoint, {
          timeout: 1000,
        })
        .then((res) => {
          if (typeof res.data.latency_ms === 'number') {
            latestLatency.value[endpoint] = res.data.latency_ms;
          }

          // normalize string to number
          if (typeof res.data.latency_ms === 'string') {
            latestLatency.value[endpoint] = parseInt(res.data.latency_ms.replace('ms', ''), 10);
          }
        })
        .catch(() => {
          //console.log(err);
        });
    });
  };

  const fetch = async () => {
    isLoading.value = true;
    datacenters.value = [];

    try {
      const response = await locationStore.reloadDatacenters();
      datacenters.value = response.datacenters;

      latencyEndpoints.value = response.datacenters
        .filter((dc) => dc.latencyEndpoint !== '')
        .map((dc) => dc.latencyEndpoint);

      if (latencyInterval.value === undefined) {
        await Promise.all(updateLatencies());
        sortDatacenters();
        latencyInterval.value = setInterval(updateLatencies, 5000);
      }
    } catch (e) {
      addNotification({
        message: (e as Error).message,
        type: 'danger',
      });
      error.value = e as Error;
    } finally {
      isLoading.value = false;
    }
  };

  onUnmounted(() => {
    if (latencyInterval.value != null) {
      clearInterval(latencyInterval.value);
    }
  });

  return {
    fetch,
    isLoading,
    data: datacenters,
    displayData: sortedDatacenters,
    error,

    latestLatency,
  };
};
